import React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { layoutModeAtom } from '../../../../atoms/v3/uiAtoms';
import { useShortcutManager } from '../../../../hooks/useShortcutManager';
import { getPlatformInfo, isTouchDevice } from '../../../../utils/platformUtils';

interface KeymapModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPageType?: 'taskv2' | 'chat' | 'me' | 'unknown';
}

interface KeymapItem {
  keys: string[];
  description: string;
  category: string;
}

/**
 * 键盘快捷键说明弹窗
 */
export const KeymapModal: React.FC<KeymapModalProps> = ({ isOpen, onClose, currentPageType = 'taskv2' }) => {
  const { t } = useTranslation('v3/tasks');
  const [isDark] = useAtom(isDarkAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const platformInfo = getPlatformInfo();
  const { getActiveCommands } = useShortcutManager();

  // 处理 ESC 键关闭弹窗
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // 根据平台和页面类型生成快捷键列表
  const getKeymapItems = (): KeymapItem[] => {
    // 创建模拟的上下文来获取当前生效的快捷键
    const mockContext = {
      layoutMode,
      currentPage: currentPageType,
      isInputFocused: false,
      activeElement: null,
      isModalOpen: false,
      isVoiceRecording: false,
      event: new KeyboardEvent('keydown')
    };

    // 从新的快捷键系统获取当前生效的快捷键
    const activeCommands = getActiveCommands(mockContext);

    // 去重：基于命令ID去重，避免重复显示
    const uniqueCommands = activeCommands.filter((command, index, array) =>
      array.findIndex(c => c.id === command.id) === index
    );

    // 根据平台选择合适的键位组合
    const selectPlatformKeys = (keys: string[]): string[] => {
      // 分离 Mac 和 Windows/Linux 键位
      const macKeys = keys.filter(key => key.toLowerCase().includes('cmd+'));
      const winKeys = keys.filter(key => key.toLowerCase().includes('ctrl+'));
      const neutralKeys = keys.filter(key =>
        !key.toLowerCase().includes('cmd+') && !key.toLowerCase().includes('ctrl+')
      );

      // 根据平台选择合适的键位
      if (platformInfo.isMacOS) {
        // Mac 平台：优先选择 cmd+ 键位，如果没有则选择中性键位
        return macKeys.length > 0 ? macKeys : neutralKeys;
      } else {
        // Windows/Linux 平台：优先选择 ctrl+ 键位，如果没有则选择中性键位
        return winKeys.length > 0 ? winKeys : neutralKeys;
      }
    };

    // 标准化按键显示的函数
    const normalizeKey = (key: string): string => {
      return key
        .replace(/cmd\+/gi, 'Cmd+')
        .replace(/ctrl\+/gi, 'Ctrl+')
        .replace(/shift\+/gi, 'Shift+')
        .replace(/alt\+/gi, 'Alt+')
        .replace(/option\+/gi, 'Option+')
        .replace(/arrowleft/gi, '←')
        .replace(/arrowright/gi, '→')
        .replace(/arrowup/gi, '↑')
        .replace(/arrowdown/gi, '↓')
        .replace(/left/gi, '←')
        .replace(/right/gi, '→')
        .replace(/up/gi, '↑')
        .replace(/down/gi, '↓')
        .replace(/space/gi, 'Space')
        .replace(/enter/gi, 'Enter')
        .replace(/esc/gi, 'Esc')
        .replace(/escape/gi, 'Esc');
    };

    // 转换为 KeymapItem 格式，根据平台选择合适的键位
    const keymapItems: KeymapItem[] = uniqueCommands.map(command => {
      const platformKeys = selectPlatformKeys(command.keys);
      return {
        keys: platformKeys.map(normalizeKey),
        description: command.description,
        category: command.category
      };
    });

    // 禁用长按帮助快捷键显示
    // if (!isTouchDevice()) {
    //   keymapItems.push({
    //     keys: [platformInfo.isMacOS ? '长按 Cmd' : '长按 Ctrl'],
    //     description: t('common:keymap.showKeymap'),
    //     category: t('common:keymap.help')
    //   });
    // }

    return keymapItems;
  };

  const keymapItems = getKeymapItems();

  const categories = Array.from(new Set(keymapItems.map(item => item.category)));

  // 渲染单个按键组合，支持拆分显示
  const renderKeyCombo = (keyCombo: string) => {
    // 拆分按键组合，例如 "Cmd+K" -> ["Cmd", "K"]
    const keys = keyCombo.split('+').map(k => k.trim());

    return (
      <div className="flex items-center space-x-1">
        {keys.map((key, index) => (
          <kbd
            key={index}
            className={`
              inline-flex items-center justify-center px-2 py-1 text-xs font-mono rounded
              ${isDark
                ? 'bg-ios-dark-gray4 text-ios-dark-gray1 border border-ios-dark-gray3'
                : 'bg-gray-100 text-gray-700 border border-gray-300'
              }
            `}
          >
            {key}
          </kbd>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 弹窗内容 */}
      <div
        className={`
          relative w-full max-w-md max-h-[90vh] flex flex-col rounded-2xl shadow-2xl
          ${isDark
            ? 'bg-ios-dark-gray5 border border-ios-dark-gray4'
            : 'bg-white border border-gray-200'
          }
        `}
      >
        {/* 标题栏 - 固定不滚动 */}
        <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
          <h2 className={`text-xl font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
            {t('keymap.title')}
          </h2>
          <div className="flex items-center space-x-2">
            {/* ESC 指示 */}
            <kbd
              className={`
                inline-flex items-center justify-center px-2 py-1 text-xs font-mono rounded
                ${isDark
                  ? 'bg-ios-dark-gray4 text-ios-dark-gray2 border border-ios-dark-gray3'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
                }
              `}
            >
              {t('common:keymap.escKey', 'Esc')}
            </kbd>
            {/* 关闭按钮 */}
            <button
              onClick={onClose}
              className={`
                p-2 rounded-full transition-colors
                ${isDark
                  ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray2'
                  : 'hover:bg-gray-100 text-gray-500'
                }
              `}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 快捷键列表 - 可滚动区域 */}
        <div className="px-6 pb-6 flex-1 overflow-y-auto">
          {categories.map(category => (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-ios-dark-gray2' : 'text-gray-600'}`}>
                {category}
              </h3>
              <div className="space-y-3">
                {keymapItems
                  .filter(item => item.category === category)
                  .map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
                        {item.description}
                      </span>
                      <div className="flex items-center space-x-1">
                        {item.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            {keyIndex > 0 && (
                              <span className={`text-xs ${isDark ? 'text-ios-dark-gray3' : 'text-gray-400'}`}>
                                /
                              </span>
                            )}
                            {renderKeyCombo(key)}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* 底部提示 - 固定不滚动 */}
        <div className={`
          px-6 py-4 border-t rounded-b-2xl flex-shrink-0
          ${isDark
            ? 'bg-ios-dark-gray6 border-ios-dark-gray4 text-ios-dark-gray3'
            : 'bg-gray-50 border-gray-200 text-gray-500'
          }
        `}>
          <p className="text-xs text-center">
            {t('keymap.inputDisabledHint')}
          </p>
        </div>
      </div>
    </div>
  );
};
